// src/components/common/Layout/AdminLayout.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useNotifications } from "../../../context/NotificationContext";
import NotificationDropdown from "../NotificationDropdown";
import LogoutModal from "../modals/LogoutModal";
import { formatPersonName } from "../../../utils/formatters";
import { onboardingApi } from "../../../api/packageApi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AdminLayout.css";

const COLLAPSE_KEY = "admin-sidebar-collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();

  const location = useLocation();
  const navigate = useNavigate();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readCollapsed);
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const [brandLogo, setBrandLogo] = useState("");

  const userMenuRef = useRef(null);

  useEffect(() => {
    let isCurrent = true;
    onboardingApi
      .getContractSettings()
      .then((response) => {
        if (isCurrent) {
          const settings = response.data || {};
          setBrandLogo(settings.brandLogo || "");
        }
      })
      .catch((error) => {
        console.error("Could not load the admin sidebar brand logo:", error);
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  /* ---------- Close user menu on outside click / Escape ---------- */

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsUserMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isUserMenuOpen]);

  /* ---------- Mobile drawer behaviour ---------- */

  useEffect(() => {
    setIsNavigationOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isNavigationOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsNavigationOpen(false);
    };
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = original;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isNavigationOpen]);

  /* ---------- Navigation ---------- */

  const navGroups = [
    {
      title: "Overview",
      items: [
        {
          path: "/admin",
          label: "Dashboard",
          icon: "fas fa-chart-pie",
        },
        {
          path: "/admin/analysis",
          label: "Analysis",
          icon: "fas fa-chart-line",
        },
      ],
    },
    {
      title: "Operations",
      items: [
        {
          path: "/admin/requests",
          label: "Store requests",
          icon: "fas fa-clipboard-list",
        },
        {
          path: "/admin/website-requests",
          label: "Website requests",
          icon: "fas fa-globe",
        },
        {
          path: "/admin/communication",
          label: "Communication",
          icon: "fas fa-comments",
          badge: unreadCount,
          aliases: ["/admin/support", "/admin/notifications"],
        },
      ],
    },
    {
      title: "Platform",
      items: [
        { path: "/admin/clients", label: "Clients", icon: "fas fa-users" },
        {
          path: "/admin/stores",
          label: "Store overview",
          icon: "fas fa-store",
        },
        { path: "/admin/packages", label: "Store Package", icon: "fas fa-box-open" },
        {
          path: "/admin/website-packages",
          label: "Website packages",
          icon: "fas fa-globe",
        },
        {
          path: "/admin/payments",
          label: "Payments",
          icon: "fas fa-credit-card",
        },
      ],
    },
    {
      title: "Settings",
      items: [
        {
          path: "/admin/onboarding-control",
          label: "Onboarding steps",
          icon: "fas fa-route",
        },
        {
          path: "/admin/website-onboarding-control",
          label: "Website Control Center",
          icon: "fas fa-globe",
        },
        {
          path: "/admin/contracts",
          label: "Contracts & branding",
          icon: "fas fa-file-signature",
        },
        {
          path: "/admin/email",
          label: "Email & notifications",
          icon: "fas fa-envelope",
        },
        {
          path: "/admin/store-database",
          label: "Store database",
          icon: "fas fa-database",
        },
        {
          path: "/admin/nginx-server",
          label: "Nginx server",
          icon: "fas fa-server",
        },
        {
          path: "/admin/audit",
          label: "Audit logs",
          icon: "fas fa-shield-alt",
        },
      ],
    },
  ];

  const allItems = navGroups.flatMap((group) => group.items);

  const matchesItem = (item) => {
    const paths = [item.path, ...(item.aliases || [])];
    return paths.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
  };

  /* ---------- User ---------- */

  const displayName = formatPersonName(user?.FullName, "Administrator");

  const getInitials = () => {
    const names = displayName.split(" ").filter(Boolean);
    if (names.length >= 2) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return (names[0]?.[0] || "A").toUpperCase();
  };

  /* ---------- Page title ---------- */

  const currentItem = allItems.find(matchesItem);
  const currentGroup = navGroups.find((g) => g.items.includes(currentItem));
  const pageTitle =
    location.pathname === "/admin/profile"
      ? "Profile"
      : currentItem?.label || "Overview";

  /* ---------- Actions ---------- */

  const handleLogout = () => {
    setShowLogoutModal(true);
    setIsUserMenuOpen(false);
  };

  const confirmLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* storage unavailable — ignore */
      }
      return next;
    });
  };

  /* ---------- Render ---------- */

  return (
    <>
      <div
        className={`admin-dashboard-container ${
          isSidebarCollapsed ? "is-collapsed" : ""
        }`}
      >
        <aside
          id="admin-navigation"
          className={`admin-sidebar ${isNavigationOpen ? "mobile-open" : ""}`}
        >
          <div className="admin-sidebar-brand">
            <Link to="/admin" className="admin-sidebar-logo" title="Toora Flex">
              {brandLogo ? (
                <img
                  className="admin-logo-image"
                  src={brandLogo}
                  alt="Toora Flex"
                  onError={() => setBrandLogo("")}
                />
              ) : (
                <span className="admin-logo-mark">TF</span>
              )}
              <span className="admin-logo-text">
                <strong>Toora Flex</strong>
                <span>Admin</span>
              </span>
            </Link>

            <button
              className="admin-sidebar-toggle"
              type="button"
              onClick={toggleSidebar}
              aria-label={
                isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              aria-pressed={isSidebarCollapsed}
            >
              <i
                className={`fas fa-angles-${isSidebarCollapsed ? "right" : "left"}`}
              ></i>
            </button>
            <button
              className="admin-mobile-sidebar-close"
              type="button"
              onClick={() => setIsNavigationOpen(false)}
              aria-label="Close navigation"
              title="Close menu"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>

          <div className="admin-sidebar-scroll">
            <nav className="admin-nav" aria-label="Admin">
              {navGroups.map((group) => (
                <div className="admin-nav-group" key={group.title}>
                  <h2 className="admin-nav-group-title">{group.title}</h2>

                  <ul className="admin-nav-list">
                    {group.items.map((item) => {
                      const isActive = matchesItem(item);
                      const hasBadge = item.badge > 0;

                      return (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            className={`admin-nav-item ${isActive ? "active" : ""}`}
                            aria-current={isActive ? "page" : undefined}
                            title={isSidebarCollapsed ? item.label : undefined}
                            onClick={() => setIsNavigationOpen(false)}
                          >
                            <i className={`admin-nav-icon ${item.icon}`}></i>
                            <span className="admin-nav-label">
                              {item.label}
                            </span>
                            {hasBadge && (
                              <span
                                className="admin-nav-badge"
                                aria-label={`${item.badge} unread`}
                              >
                                {item.badge > 99 ? "99+" : item.badge}
                              </span>
                            )}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>

        </aside>

        {isNavigationOpen && (
          <button
            className="admin-sidebar-backdrop"
            type="button"
            aria-label="Close navigation"
            onClick={() => setIsNavigationOpen(false)}
          />
        )}

        <main className="admin-main-content">
          <header className="admin-top-bar">
            <button
              className="admin-mobile-nav-toggle"
              type="button"
              aria-label={
                isNavigationOpen ? "Close navigation" : "Open navigation"
              }
              aria-expanded={isNavigationOpen}
              aria-controls="admin-navigation"
              onClick={() => setIsNavigationOpen((open) => !open)}
            >
              <i
                className={`fas fa-${isNavigationOpen ? "times" : "bars"}`}
              ></i>
            </button>

            <div className="admin-page-heading">
              <nav className="admin-breadcrumb" aria-label="Breadcrumb">
                <Link to="/admin">Admin</Link>
                {currentGroup && (
                  <>
                    <i className="fas fa-chevron-right" aria-hidden="true"></i>
                    <span>{currentGroup.title}</span>
                  </>
                )}
              </nav>
              <h1 className="admin-page-title">{pageTitle}</h1>
            </div>

            <div className="admin-top-actions">
              <NotificationDropdown admin />
              <div className="admin-top-user-container" ref={userMenuRef}>
                {isUserMenuOpen && (
                  <div className="admin-user-menu admin-top-user-menu" role="menu">
                    <div className="admin-user-menu-head">
                      <strong>{displayName}</strong>
                      <span>Administrator account</span>
                    </div>
                    <Link
                      to="/admin/profile"
                      role="menuitem"
                      className="admin-user-menu-item admin-user-profile-link"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <i className="fas fa-user"></i>
                      <span>Profile</span>
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      className="admin-user-menu-item danger"
                      onClick={handleLogout}
                    >
                      <i className="fas fa-sign-out-alt"></i>
                      <span>Sign out</span>
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="admin-top-user"
                  onClick={() => setIsUserMenuOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                  aria-label={`Administrator menu for ${displayName}`}
                >
                  <span className="admin-avatar">{getInitials()}</span>
                  <span className="admin-top-user-info">
                    <strong>{displayName}</strong>
                    <small>Administrator</small>
                  </span>
                  <i
                    className={`fas fa-chevron-${isUserMenuOpen ? "up" : "down"}`}
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            </div>
          </header>

          <div className="admin-page-content">{children}</div>

          <footer className="admin-footer">
            <span>Toora Flex Administration</span>
            <span>© {new Date().getFullYear()}</span>
          </footer>
        </main>
      </div>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
      />
      <ToastContainer
        position="top-right"
        autoClose={3500}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default AdminLayout;
